import { fill, t, type StringKey } from '../strings';
import { saleQuantityProblems } from '../catalog/catalog';
import { documentStatusPill, heldDraftPill } from '../documents/statuses';
import type { StatusResolver } from '../statusDefinitions';
import { COUNTERPARTY, type Counterparty } from '../documents/counterparty';
import { recordKindName } from '../documents/documentNames';
import { inForceThrough } from '../linkRow';
import { HIRE_WINDOW_ROUTE } from '../hr/hire';
import { defaultOrigin } from '../deals/dealModel';
/**
 * The four record pages (piece 13). 🔨 Tile order is decided once, because the
 * Tile Canvas locks positions: a salesperson opens a company before quoting, and
 * Account standing and Work are what they came for. 🔒 The Person page carries
 * no HR tile: those are sections of the Employee page in HR (decision 3180).
 */

export const RECORD_ROUTES = {
  CompanyRecord: { list: '/companies', record: '/companies/:id', module: 'Company' },
  PersonRecord: { list: '/people', record: '/people/:id', module: 'Person' },
  LocationRecord: { list: '/locations', record: '/locations/:id', module: 'Location' },
  SiteRecord: { list: '/sites', record: '/sites/:id', module: 'Site' },
  // Piece 17: the catalog, the fifth record on the pattern; its module is Item and it sits under INVENTORY.
  Item: { list: '/items', record: '/items/:id', module: 'Item' },
  // Piece 19: the Deal record page, the sixth record on the pattern, under CRM.
  Deal: { list: '/deals', record: '/deals/:id', module: 'Deal' },
  // Piece 20: the Lead, a claim before it is a relationship; its module is the Deal's.
  Lead: { list: '/leads', record: '/leads/:id', module: 'Deal' },
  // Piece 22: the Purchase Order, a document page rather than a tile canvas, under its own module.
  PurchaseOrder: { list: '/purchase-orders', record: '/purchase-orders/:id', module: 'PurchaseOrder' },
  // Piece 22: the request for quotation, a record of its own under PURCHASING rather than a
  // frame on the Deal alone (decision 0488). Its module is the Purchase Order's, because a
  // buyer who may raise an order is the person who asks vendors for a price.
  RFQ: { list: '/rfqs', record: '/rfqs/:id', module: 'PurchaseOrder' },
  // Piece 18: the bill of materials, a record of its own so one can stand outside a Deal
  // (decision 0488). Its module is the Deal's, because a BOM is what a project is made of.
  BOM: { list: '/boms', record: '/boms/:id', module: 'Deal' },
  // Piece 22: the Quote, the first accounting document with a page, under its own Quote module.
  Quote: { list: '/quotes', record: '/quotes/:id', module: 'Quote' },
  // Piece 22: the Work Order, the first record under OPERATIONS, on the same pattern (built 2026-09-14).
  WorkOrder: { list: '/work-orders', record: '/work-orders/:id', module: 'WorkOrder' },
  // Piece 22, the sales side: four document lists under ACCOUNTING. Each record route is
  // registered here so the list's rows have somewhere to open; the pages themselves arrive
  // with the document-page stream, and the route answers Not Found until they do.
  SalesOrder: { list: '/sales-orders', record: '/sales-orders/:id', module: 'SalesOrder' },
  Invoice: { list: '/invoices', record: '/invoices/:id', module: 'Invoice' },
  CreditNote: { list: '/credit-notes', record: '/credit-notes/:id', module: 'Accounting' },
  Payment: { list: '/payments', record: '/payments/:id', module: 'Accounting' },
  // Piece 22, the purchase and stock side (built 2026-09-15): each carries its own list here; the record pages arrive with the page stream.
  Bill: { list: '/bills', record: '/bills/:id', module: 'Accounting' },
  VendorCredit: { list: '/vendor-credits', record: '/vendor-credits/:id', module: 'Accounting' },
  StockCount: { list: '/stock-counts', record: '/stock-counts/:id', module: 'Item' },
  StockTransfer: { list: '/stock-transfers', record: '/stock-transfers/:id', module: 'Item' },
  StockRevaluation: { list: '/stock-revaluations', record: '/stock-revaluations/:id', module: 'Item' },
  // Piece 23, decision 0761 (T139): the Manual Journal, a list and a document page like every other accounting
  // document, at the address the Manual Journal screen has always had. Its module is the Accounting one the nav row reads.
  JournalEntry: { list: '/accounting/journal', record: '/accounting/journal/:id', module: 'Accounting' },
  // 🔒 Piece 29, decision 2910: the Service Reports list under OPERATIONS, every visit's report with its state, on the
  // Work Order module's read. A row opens its visit's own page, which is the report's page, through its record route.
  ServiceReport: { list: '/service-reports', record: '/service-reports/:id', module: 'WorkOrder' },
} as const;

export type PagedRecord = keyof typeof RECORD_ROUTES;

/** The global search's groups as the record types an event links to (decision 0275; the Item withdrawn by decision 0286). */
export const LINKED_GROUP_TYPES: Record<string, 'CompanyRecord' | 'PersonRecord' | 'LocationRecord' | 'Deal'> = {
  Companies: 'CompanyRecord',
  People: 'PersonRecord',
  Locations: 'LocationRecord',
  Deals: 'Deal',
};

/** The record type's own glyph, the one its nav item carries, drawn left of the Title Banner's title where there is room (product owner, 2026-09-10). */
export const RECORD_ICONS: Partial<Record<PagedRecord, 'company' | 'person' | 'location' | 'site' | 'lead' | 'deal' | 'item' | 'quote' | 'workOrder' | 'accounting' | 'terms' | 'file' | 'retry' | 'amountInBankFeed' | 'documents' | 'rfq' | 'bom'>> = {
  CompanyRecord: 'company',
  PersonRecord: 'person',
  LocationRecord: 'location',
  SiteRecord: 'site',
  Lead: 'lead',
  Deal: 'deal',
  Item: 'item',
  Quote: 'quote',
  WorkOrder: 'workOrder',
  // The collapsed list bar draws the list's own glyph and nothing else, so a register with no icon is a bar nobody can read (decision 0408).
  PurchaseOrder: 'accounting',
  // Each sales-side document carries the glyph its own navigation row already carries, so the bar and the bar's row read alike.
  SalesOrder: 'terms',
  Invoice: 'file',
  CreditNote: 'retry',
  Payment: 'amountInBankFeed',
  // Piece 24: the Bill joins the frame with its own page, so its bar has a glyph to draw.
  Bill: 'accounting',
  // Decision 0761: the Manual Journal's bar draws the glyph its navigation row carries.
  JournalEntry: 'documents',
  // Decision 1052: the RFQ and the bill of materials joined the frame, and each bar draws its navigation row's glyph.
  RFQ: 'rfq',
  BOM: 'bom',
  // Decision 2910: the Service Reports list's bar draws the Work Order's glyph, the record its reports belong to.
  ServiceReport: 'workOrder',
};

/**
 * 🔒 The records that render as a list-and-detail page: the list pane on the
 * left, the record on the right, one URL per record (decision 0408). The
 * catalog was first under decision 0318 and the other seven followed.
 * 💰 Deal and Lead are absent on purpose: each of those two lists carries a
 * second whole-width rendering — the pipeline board and the Lead queues — that
 * a third of the width does not hold, and piece 13 carries the open question.
 */
export const LIST_DETAIL_RECORDS: readonly PagedRecord[] = [
  'CompanyRecord',
  'PersonRecord',
  'LocationRecord',
  'SiteRecord',
  'Item',
  'Quote',
  'PurchaseOrder',
  'WorkOrder',
  'SalesOrder',
  'Invoice',
  'CreditNote',
  'Payment',
  // Piece 24: the Bill's own page arrived with the money-out stream, so it leaves the list-only set.
  'Bill',
  // Decision 7580 (T3122): the Vendor Credit's own page, reversing decision 1158.
  'VendorCredit',
  // Piece 22: the three stock documents gained their own pages, so each list opens its rows rather than sending them to Not Found (decision 0441).
  'StockTransfer',
  'StockCount',
  'StockRevaluation',
  // Decision 0761 (T139): the Manual Journal's list beside the entry's own page, as every accounting document's is.
  'JournalEntry',
  // Decision 1052 (T317): the RFQ's comparison and the bill of materials' page left the route table's list-only exception.
  'RFQ',
  'BOM',
];

export function isListDetailRecord(recordType: PagedRecord): boolean {
  return LIST_DETAIL_RECORDS.includes(recordType);
}

/**
 * 🔒 The accounting documents whose record page has two modes — an edit form
 * and the record drawn on screen (decision 0409). + New opens the page in edit
 * mode; no document is created through a modal. Bill and Change Order join
 * this list as each is built.
 * 🔨 The Payment is on it without a printed sheet, because it belongs to no
 * print template family and Region 8's item columns describe lines it has
 * none of. Its view mode draws the recorded money instead, and piece 22
 * carries the open question about a printed receipt.
 */
export const DOCUMENT_PAGE_RECORDS: readonly PagedRecord[] = ['Quote', 'PurchaseOrder', 'WorkOrder', 'SalesOrder', 'Invoice', 'CreditNote', 'Payment', 'Bill'];

export function isDocumentPageRecord(recordType: PagedRecord): boolean {
  return DOCUMENT_PAGE_RECORDS.includes(recordType);
}

/** The id a document's route carries while it has none: `<list>/new` is the Draft + New opens. */
export const NEW_DOCUMENT_ID = 'new';

export type DocumentPageMode = 'view' | 'edit';

/** The mode the URL names; a document that does not exist yet is always in edit mode. */
export function documentPageMode(asked: string | null | undefined, recordId: string): DocumentPageMode {
  return recordId === NEW_DOCUMENT_ID || asked === 'edit' ? 'edit' : 'view';
}

/** Which piece builds a tile; a tile owned elsewhere is absent until that piece lands. */
export type TileOwner = 'here' | 'later';

/**
 * How a tile draws its rows: one of the Tile's item-type renderers of Composite
 * and data-display components. 🔨 A record page reuses those renderers and
 * never invents a row of its own — Contacts is the Person item over the
 * Attendee Avatar, Sites is the Location item, a note is the Note item with
 * its author and date, and General Information is the Label/Value grid.
 * 💰 Every tile drew its rows as one-line notes joined with dashes, so the
 * pages matched neither the source figures nor the Component Test Page's own
 * sample tiles (found while building, 2026-09-09).
 * 🔨 A tile listing documents draws through `documentRow`, one grid with four
 * columns — date, kind, pill, name — so every row of the list lines up
 * (decision 0591).
 * 🔒 A tile listing a phone or an email draws through `contactRow`, the stacked
 * standard of decision 0623: the kind's glyph, its human-English name over the
 * job it belongs to, the value, and the one action the line offers.
 * 🔒 The Site page's certificates tile draws through `certificateCard`, a small fact
 * card headed by the gold badge and the state, with its ⋯ menu (decision 7440).
 * 🔒 The Person page's Employments tile draws through `jobTimeline`, one job per employment on a
 * rule with a dot, its titles beneath (decision 7480).
 */
export type TileDraws = 'labelValueGrid' | 'person' | 'location' | 'file' | 'note' | 'timeline' | 'statusOnly' | 'fullWidthRow' | 'linkRow' | 'eventBooking' | 'datedRole' | 'documentRow' | 'contactRow' | 'certificateCard' | 'jobTimeline';

/**
 * 🔒 A tagged record's kind in plain words, for the line that has no name to print (T781):
 * `WorkOrder` reads Work Order and `CompanyRecord` reads Company. An event's Tagged record
 * line never prints a type name as it is stored, and never an id. The one name is
 * `recordKindName` (T2107; decision 2841).
 */
export function taggedRecordKindWords(recordType: string): string {
  return recordKindName(recordType);
}

/** The whole event an Upcoming Events row carries, because Event Booking takes the event and never its fields one by one (piece 4). */
export interface TileEventRow {
  id: string;
  subject: string;
  eventType: string;
  startsAt: string;
  endsAt: string | null;
  timeZone: string;
  ownerName: string;
  ownerPersonId?: string | null;
  place: string | null;
  /** The place's street address on one line, what Open in Maps hands over (product owner, 2026-09-10). */
  placeAddress?: string | null;
  siteId?: string | null;
  locationId?: string | null;
  notes?: string | null;
  meetingLink?: string | null;
  callNumber?: string | null;
  taggedRecordType?: string | null;
  taggedRecordId?: string | null;
  taggedRecordLabel?: string | null;
  /** The event's version as the tile read it (decision 10670; T3634), which the window's save sends back so a stale save is refused. */
  version?: number | null;
  attendees?: { name: string; avatarColor: string; attending: 'NeedsAction' | 'Accepted' | 'Declined' | 'Tentative' }[];
}

/**
 * The action a tile's header carries, where the tile has one to give. The
 * label is the source figure's: + Add Note, + Add Contact, ↑ Upload, + Add
 * Event. A tile with no action shows none, because its rows are managed from
 * their own records.
 */
export type TileActionKind =
  | 'editRecord'
  | 'editCustomFields'
  /** The Company page's Account standing tile: the terms, the credit limit and the hold, written together (pieces 10 and 13). */
  | 'editAccountStanding'
  | 'addNote'
  | 'addEmployed'
  | 'hire'
  | 'addSite'
  | 'addOccupant'
  | 'addEmployment'
  | 'addTag'
  | 'addEvent'
  | 'upload'
  | 'addContactMethod'
  | 'checkIn'
  | 'addAttribute'
  | 'addSupplier'
  | 'stockAtSite'
  | 'addTeamMember'
  | 'addDealContact'
  | 'addAccountRole'
  | 'addCertificate'
  | 'addRequirement'
  /** The Site page's Roles tile: one dated SiteRole row (built 2026-09-12). */
  | 'addSiteRole'
  /** Piece 9's NDA upload, from the Company page's Access & agreements tile (found while building 2026-09-09, built 2026-09-12). */
  | 'addNda'
  /** Piece 22: the categories a vendor represents, from the vendor's Work tile (decision 0158). */
  | 'addVendorRep'
  /** Piece 22: + New Quote on the Deal page's Quotes tile and the Company page's banner, opening the New Quote modal with the record filled in. */
  | 'newQuote'
  /** Piece 22, decision 10882: + New Work Order on the Won Project or Service Deal's Documents tile, opening the job's form with the Deal and its Account handed over. */
  | 'newWorkOrder'
  /**
   * Piece 22, decisions 0464 and 0614: the Person page's Skills tile, recording
   * what this person can do — the whole skill, or one sub-skill beneath it.
   */
  | 'addSkill'
  /** Piece 23, decision 0495: + New RFQ on the Deal page's RFQs tile, opening the same modal the bill of materials' footer does. */
  | 'newRfq'
  /** Piece 19, decision 0497: + New Deal on a Company's banner, which is where work enters; its label alone since it opens the New Deal page (decision 2510). */
  | 'newDeal'
  /** 🔒 Decision 2972: the Company page's Mailboxes tile, one dated `CompanyMailbox` row. */
  | 'addMailbox'
  /** 🔒 Decision 6400: the Person page's Education tile, opening the Add School window from the tile head as + Add Employment does. */
  | 'addEducation'
  /** 🔒 T2352: the Person page's Confidentiality Roles tile, opening the Grant Role window from the tile head. */
  | 'grantConfidentialityRole';

/**
 * 🔒 How many History entries stand before the list scrolls inside for the rest
 * (T588; decision 1404, amending 0500's ten-row pager), on a record page's tile
 * and a document page's panel alike. The server caps the tile at forty rows, and
 * every one that arrived is drawn.
 */
export const HISTORY_WINDOW_ROWS = 5;

/**
 * 🔒 How many file rows a Files tile draws before it says how many more it
 * holds, and how many the server sends (product owner, 2026-09-20). 💰 The tile
 * stopped at twenty-five rows and said nothing about it, so a record with forty
 * files read as a record with twenty-five. The cap is the read's, and a record
 * past it still stops — stated, the way History's forty does.
 */
export const FILES_PAGE_SIZE = 10;
export const FILES_TILE_CAP = 100;

export interface TileSpec {
  key: string;
  label: string;
  owner: TileOwner;
  /** The item-type renderer the tile's rows draw through. */
  draws: TileDraws;
  /** Columns on the record page's three-column canvas; the canvas locks the position and the tile keeps its span. */
  span: 1 | 2 | 3;
  /** Height in blocks, one block being one column width (piece 4); every record tile is one block unless stated. */
  height?: number;
  /** The header action, where the tile has one. */
  action?: TileActionKind;
  /** True where a tile's content is withheld until the page reveals it; no tile carries it since the Person page lost its HR tiles (decision 3180). */
  masked?: boolean;
  /** True where the tile renders on the Organization's own Company only, or never on it. */
  ownCompanyOnly?: boolean;
  notOnOwnCompany?: boolean;
  /** Where the tile's rows draw: on the canvas, or in the record banner as pills and chips (decision 0307). */
  placement?: 'canvas' | 'banner';
  /**
   * 🔒 The tile folds to its own heading behind a chevron, and the choice is
   * remembered for every record page (decision 0500). Every canvas tile
   * carries it since decision 6211, so a page condenses to what the viewer
   * wants open; a folded tile keeps its count and its Add action.
   */
  collapsible?: boolean;
  /**
   * 🔒 How many rows the tile draws before it asks, with the count stated: a
   * Files tile's ten (decision 0500's pager).
   */
  pageSize?: number;
  /**
   * 🔒 How many entries stand before the list scrolls inside for the rest
   * (T588; decision 1404). History carries it: the server caps its rows at
   * forty, and forty rows unfolded is a page somebody has to scroll past.
   */
  windowRows?: number;
}

/** The label each action's button reads, as the source figures draw them. */
export const TILE_ACTION_LABELS: Readonly<Record<TileActionKind, string>> = {
  editRecord: 'Edit',
  editCustomFields: 'Edit',
  editAccountStanding: 'Edit',
  addNote: '+ Add Note',
  addEmployed: '+ Add Contact',
  hire: '+ Hire',
  addSite: '+ Add Site',
  addOccupant: '+ Add Occupant',
  addEmployment: '+ Add Employment',
  addTag: '+ Add Tag',
  addEvent: '+ Add Event',
  upload: '↑ Upload',
  addContactMethod: '+ Add',
  checkIn: '+ Check In',
  addAttribute: '+ Add Attribute',
  addSupplier: '+ Add Supplier',
  stockAtSite: '+ Stock at Site',
  addTeamMember: '+ Add Team Member',
  addDealContact: '+ Add Contact',
  addAccountRole: '+ Add Role',
  addCertificate: '+ Add Certificate',
  addRequirement: '+ Add Requirement',
  addSiteRole: '+ Add Role',
  addNda: '+ Upload NDA',
  addVendorRep: '+ Add Category',
  newQuote: '+ New Quote',
  newWorkOrder: '+ New Work Order',
  newRfq: '+ New RFQ',
  newDeal: '+ New Deal',
  addSkill: '+ Add Skill',
  addMailbox: '+ Add Mailbox',
  addEducation: '+ Add School',
  grantConfidentialityRole: '+ Grant Role',
};

/**
 * The Item page's eleven tiles (piece 17; decisions 0160, 1940), in the order the piece
 * numbers them. 🔨 Stock by Site is one tile and is absent entirely when the
 * Item is a Service (decision 0153); Pricing & Margin's cost fields are gated
 * at the data-access layer through canSeeField, never by omitting the tile
 * (decision 0125). The page carries no Delete: an Item ends by status.
 */
export const ITEM_TILES: readonly TileSpec[] = [
  // Two blocks tall: the Item's fields fill one and its image stands beside them (decision 0269).
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 2, height: 2, action: 'editRecord', collapsible: true },
  // Sales and purchase information stand apart, the way the product owner's example draws them (decision 0277); cost stays the accounting audience's (0276).
  { key: 'salesInformation', label: 'Sales Information', owner: 'here', draws: 'labelValueGrid', span: 1, collapsible: true },
  { key: 'purchaseInformation', label: 'Purchase Information', owner: 'here', draws: 'labelValueGrid', span: 1, collapsible: true },
  // Units & Sale Rules read on General Information since 2026-09-11 (decision 0296).
  { key: 'attributes', label: 'Attributes', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addAttribute', collapsible: true },
  { key: 'stockBySite', label: 'Stock by Site', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'stockAtSite', collapsible: true },
  { key: 'sourcing', label: 'Sourcing', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addSupplier', collapsible: true },
  { key: 'whereUsed', label: 'Where Used & Replacement', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  // 🔒 Every line naming the Item, newest first, the whole canvas wide; a panel reading its own route draws it (T1254; decision 1940).
  { key: 'transactions', label: 'Transactions', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'itemFiles', label: 'Files & Knowledge Base', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 2, action: 'addNote', collapsible: true },
  // 🔨 No Upcoming Events tile: nothing is booked against a part (decision 0286, withdrawing the Item from decision 0275).
];

/**
 * The Deal page (piece 19; figures 81 to 85): Deal Progress as a checklist off
 * the gate data, one StageCard construction for the current stage with Intent
 * as BANT, the value beside its floor and the floor's arithmetic, Next Action
 * as a list, the Deal Team with its two gates, the buying committee, the five
 * document frames on the canvas, the file section a Deal's documents are
 * released through, and the History tile (decisions 0048, 0114, 0206, 0207,
 * 0208, 0233). 🔒 The page reads as six bands (decision 1215): the Deal Team
 * beside the Contacts, the stage cards, the documents, the files beside the
 * events, the notes beside the custom fields, and History. The bills of
 * materials and the RFQ's list read inside the Evaluation card, which is where
 * they are made (decision 0595).
 */
export const DEAL_TILES: readonly TileSpec[] = [
  // 🔒 No General Information tile (decision 0722): each of its rows reads in one place of its own —
  // the customer in the kind line, the owner as the team's first row, the stage on Deal Progress.
  // 🔒 The team takes the first slot, above the stage cards (decisions 0595, 0722), and the
  // Contacts stand beside it in one band at one height (decision 1215).
  // Product owner, 2026-09-20: *"I think deal team should be at top under the general information."*
  { key: 'dealTeam', label: 'Deal Team', owner: 'here', draws: 'person', span: 2, action: 'addTeamMember', collapsible: true },
  { key: 'dealContacts', label: 'Contacts', owner: 'here', draws: 'person', span: 1, action: 'addDealContact', collapsible: true },
  { key: 'dealProgress', label: 'Deal Progress', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'stageCard', label: 'Stage Card', owner: 'here', draws: 'fullWidthRow', span: 2, collapsible: true },
  // 🔒 The value reads in the banner's column (decision 0665); the floor and the BOM cost read nowhere on the page (decision 0722).
  { key: 'nextAction', label: 'Next Action', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  // 🔨 No Bill of materials tile and no RFQs tile (decision 0595): the lists and their two controls
  // are inside the Evaluation card, under the BOM Built and RFQ Issued rows, which is where the
  // work is done. The RFQ's list is still one of the six the page assembles; it reads on the card.
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'quotes', label: 'Quotes', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'newQuote', collapsible: true },
  { key: 'salesOrders', label: 'Sales Orders', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'invoices', label: 'Invoices', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'purchaseOrders', label: 'Purchase Orders', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'workOrders', label: 'Work Orders', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'newWorkOrder', collapsible: true },
  /** 🔒 The file section a Deal's documents are released through: the one shared File row per file, Release then Share on each (piece 19; decision 6040). */
  { key: 'dealFiles', label: 'Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'upcomingEvents', label: 'Upcoming Events', owner: 'here', draws: 'eventBooking', span: 1, action: 'addEvent', collapsible: true },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 1, action: 'addNote', collapsible: true },
  // 🔒 Custom fields stand beside the notes since decision 1215; they followed the team from decision 0595.
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editCustomFields', collapsible: true },
  { key: 'history', label: 'History', owner: 'here', draws: 'timeline', span: 3, collapsible: true, windowRows: HISTORY_WINDOW_ROWS },
];

/**
 * The Lead page (piece 20), its tiles locked in this order: General
 * information with the submitted text beside a suggested match, Custom
 * fields, the three StageCards, Notes, Files, Events and Checklist. Convert
 * to Company, Log Call Attempt and Mark Not a Fit sit in the page header.
 */
export const LEAD_TILES: readonly TileSpec[
] = [
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 2, height: 2, action: 'editRecord', collapsible: true },
  // One column of three cards beside the general information, three blocks tall (decision 0277).
  { key: 'leadStageCards', label: 'Stage Cards', owner: 'here', draws: 'fullWidthRow', span: 1, height: 3, collapsible: true },
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, height: 2, action: 'editCustomFields', collapsible: true },
  { key: 'internalNotes', label: 'Notes', owner: 'here', draws: 'note', span: 1, action: 'addNote', collapsible: true },
  { key: 'leadFiles', label: 'Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'upcomingEvents', label: 'Events', owner: 'here', draws: 'eventBooking', span: 1, action: 'addEvent', collapsible: true },
  { key: 'checklist', label: 'Checklist', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
];

/**
 * The content the Quote's page answer carries (piece 22): the header facts, the
 * lines, the version history, then the notes, files and events every record
 * carries. The status pill and the approval pill draw in the banner, and the
 * footer carries the one primary action the status decides (product owner,
 * 2026-09-14).
 *
 * 🔨 It is no longer a canvas layout. The Quote draws its own document page
 * (decision 0470), which reads the lines, the versions, the files, the notes
 * and the history from this table and draws them as fixed blocks.
 * 💰 Custom fields, flags and upcoming events are still answered here and no
 * page draws them, which is one wasted read per load and the price of leaving
 * the Quote's routes untouched.
 */
export const QUOTE_TILES: readonly TileSpec[] = [
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 2, action: 'editRecord', collapsible: true },
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editCustomFields', collapsible: true },
  { key: 'lines', label: 'Lines', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  { key: 'versions', label: 'Version History', owner: 'here', draws: 'fullWidthRow', span: 2, collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 1, action: 'addNote', collapsible: true },
  { key: 'quoteFiles', label: 'Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'upcomingEvents', label: 'Upcoming Events', owner: 'here', draws: 'eventBooking', span: 1, action: 'addEvent', collapsible: true },
  { key: 'history', label: 'History', owner: 'here', draws: 'timeline', span: 3, collapsible: true, windowRows: HISTORY_WINDOW_ROWS },
];

/** The two the Quote's answer reports as its own constructions, the lines and the version history; no page reads it since the Quote drew its own (decision 0470). */
export const QUOTE_CUSTOM_TILES: readonly string[] = ['lines', 'versions'];


/**
 * The document tiles the canvas carries, each filled from its own records'
 * `dealId`. 🔨 Five, not six, since decision 0595: the RFQ's list draws inside
 * the Evaluation card under RFQ Issued, beside the control that raises one, so
 * the sixth list is on the page and is not a tile.
 */
export const DEAL_DOCUMENT_TILES: readonly string[] = ['quotes', 'salesOrders', 'invoices', 'purchaseOrders', 'workOrders'];

/**
 * The Work Order page (piece 22, built 2026-09-14): the job's facts, the
 * labour lines with their workers as chips, the parts fitted, the travel line
 * with its snapshot, then the notes, the booking and the history. The lines,
 * the materials and the travel draw through the page's own constructions; the
 * asset table and the checklist arrive with Work orders and service pricing.
 */
export const WORK_ORDER_TILES: readonly TileSpec[] = [
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 2, collapsible: true },
  { key: 'travel', label: 'Travel', owner: 'here', draws: 'labelValueGrid', span: 1, collapsible: true },
  // 🔒 Decision 6020 (T2787): Job Progress, the status steps and the visit, report and invoice milestones, above the Labor Lines.
  { key: 'progress', label: 'Job Progress', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  // 🔒 Decision 2610 (T1169): the Labor Lines sit two columns wide beside the Job Cost tile, and take the third back where the viewer reads no cost.
  { key: 'lines', label: 'Labor Lines', owner: 'here', draws: 'fullWidthRow', span: 2, height: 2, collapsible: true },
  { key: 'jobCost', label: 'Job Cost', owner: 'here', draws: 'labelValueGrid', span: 1, height: 2, collapsible: true },
  // 🔒 Decision 1908 (T1157, T1201): the job's visits, each with its window, lead, crew and state, and Book Another Visit.
  { key: 'visits', label: 'Visits', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  // 🔒 Decision 2910: each visit's Service Report with its state and every invoice raised from the job; a job with no visit keeps its own sheet.
  { key: 'documents', label: 'Documents', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  // 🔒 Decision 1908 (T1201): every time entry on the job, the typed hours beside them, the totals per line and for the job, Record Time and Raise a Correction.
  { key: 'labour', label: 'Labor', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  { key: 'materials', label: 'Materials', owner: 'here', draws: 'fullWidthRow', span: 2, collapsible: true },
  // 🔒 Decision 0765 (T129): the Deal's team as it stood when the job was created, beside the parts it fits.
  { key: 'dealTeam', label: 'Deal Team', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 1, action: 'addNote', collapsible: true },
  { key: 'upcomingEvents', label: 'Booking', owner: 'here', draws: 'eventBooking', span: 2, action: 'addEvent', collapsible: true },
  { key: 'history', label: 'History', owner: 'here', draws: 'timeline', span: 3, collapsible: true, windowRows: HISTORY_WINDOW_ROWS },
];

/**
 * 🔒 The Work Order's tiles for one viewer (decision 2610; T1169): the Job Cost
 * tile stands beside the Labor Lines for a viewer who reads every worker's cost,
 * and is absent for anybody else, the lines then three columns wide.
 */
export function workOrderTiles(facts: { seesCost: boolean }): TileSpec[] {
  if (facts.seesCost) return [...WORK_ORDER_TILES];
  return WORK_ORDER_TILES.filter((tile) => tile.key !== 'jobCost').map((tile) => (tile.key === 'lines' ? { ...tile, span: 3 as const } : tile));
}

/** The tiles an Item page draws: a Service has no Stock by Site tile, absent rather than empty (decision 0153). */
export function itemTiles(facts: { isService: boolean }): TileSpec[] {
  return ITEM_TILES.filter((tile) => !(facts.isService && tile.key === 'stockBySite'));
}

/**
 * The Company page's locked order (decision 0185). Custom fields keeps its place
 * directly under General Information, ahead of every list tile.
 */
export const COMPANY_TILES: readonly TileSpec[] = [
  /**
   * 🔒 General Information takes the first slot again (T170). The website, the
   * main phone, the parent company and our own terms read here, because the
   * title bar standard carries no facts row for them to read in.
   * 💰 The tile was dropped by decision 0335 on the grounds that the banner held
   * those facts; it does not hold them any more. The tile carries no Edit of its
   * own: the standard puts the record's pencil in the title bar.
   */
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 1, collapsible: true },
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editCustomFields', collapsible: true },
  // A tile that edits carries an Edit control, the way the Item page's does (piece 13): the terms, the limit and the hold.
  { key: 'accountStanding', label: 'Account Standing', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editAccountStanding', notOnOwnCompany: true, collapsible: true },
  /** Piece 22: the company's work orders newest first, with the RFQs and purchase orders sent to them in the same list. */
  // 🔨 A document list draws through the document row's own grid, not the shared full-width row (decision 0591).
  { key: 'work', label: 'Work', owner: 'here', draws: 'documentRow', span: 1, action: 'addVendorRep', collapsible: true },
  /**
   * 🔒 The customer half of Work (decision 0496): the quotes, sales orders and
   * invoices raised against this company, newest first. 💰 A quote could be
   * raised from this very page — the banner carries + New Quote — and then
   * appeared nowhere on it, because Work is the vendor half and nothing drew
   * the other one (product owner, 2026-09-18).
   */
  // 🔨 No action of its own: the banner already carries + New Quote on a customer, and two
  // buttons raising the same document is two behaviours wearing one name.
  { key: 'sales', label: 'Sales', owner: 'here', draws: 'documentRow', span: 1, collapsible: true },
  { key: 'contacts', label: 'Contacts', owner: 'here', draws: 'person', span: 2, action: 'addEmployed', collapsible: true },
  { key: 'sites', label: 'Sites', owner: 'here', draws: 'location', span: 1, action: 'addSite', collapsible: true },
  /**
   * 🔒 Decision 7501 (T3102): the companies this one owns, each a link with its
   * percent and since date, the ended ones under Past. Earned by
   * `CompanyOwnershipLink.ownerCompanyId` (amending 0185's walk) and drawn only
   * where the company owns or has owned one, our own Company included.
   */
  { key: 'subsidiaries', label: 'Subsidiaries', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  /**
   * 🔒 Decision 2972 (T2052): the company's mailboxes that are no person's, each
   * drawn as a contact line with its kind as the name. 🔨 Absent on our own
   * record, whose mail goes from the module's mailbox (decision 0791).
   */
  { key: 'mailboxes', label: 'Mailboxes', owner: 'here', draws: 'contactRow', span: 1, action: 'addMailbox', notOnOwnCompany: true, collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 1, action: 'addNote', collapsible: true },
  /** Piece 28: a link into the asset tree from each active `AssetCompanyLink`, never a copy of the tree; a reader with no read on the asset records is served no tile. */
  { key: 'assets', label: 'Assets', owner: 'here', draws: 'linkRow', span: 1, collapsible: true },
  { key: 'accessAndAgreements', label: 'Access & Agreements', owner: 'here', draws: 'timeline', span: 1, action: 'addNda', collapsible: true },
  { key: 'companyFiles', label: 'Company Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'upcomingEvents', label: 'Upcoming Events', owner: 'here', draws: 'eventBooking', span: 1, action: 'addEvent', collapsible: true },
  /**
   * Piece 18: the company's Deal rows, most recent first, each gated by whether
   * the viewer can see that Deal. 🔨 Absent on the Organization's own record,
   * which is nobody's account: we raise no Deal against ourselves (decision 0592).
   */
  { key: 'associatedDeals', label: 'Associated Deals', owner: 'here', draws: 'fullWidthRow', span: 2, notOnOwnCompany: true, collapsible: true },
  /**
   * Piece 20: the Account Roles panel; Sales Person reads and writes
   * assignedSalesRepEmploymentId, the rest are CompanyRoleLink rows, labels that
   * grant no access. 🔨 Absent on our own record, which has no sales rep and no
   * account contacts of its own (decision 0592).
   */
  { key: 'accountRoles', label: 'Account Roles', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addAccountRole', notOnOwnCompany: true, collapsible: true },
  /**
   * 🔒 Piece 21, decision 0971: the change-of-data requests filed against the
   * company. An outside login's tile lists its own; the account owner and a
   * Company writer decide each Open row. 🔨 Absent on our own record, which
   * nobody outside files against. It draws through its own panel, because a row
   * carries two decisions and the row registry offers one.
   */
  { key: 'changeRequests', label: 'Change Requests', owner: 'here', draws: 'fullWidthRow', span: 1, notOnOwnCompany: true, collapsible: true },
  /** 🔒 Decision 5382: each undecided Differs mark of a validation check, newest first, read from its own route with Accept and Dismiss. */
  { key: 'suggestions', label: 'Suggestions', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'history', label: 'History', owner: 'here', draws: 'timeline', span: 3, collapsible: true, windowRows: HISTORY_WINDOW_ROWS },
];

/** The two conditional tiles, on a vendor and on a customer (decision 0166). */
export const COMPANY_CONDITIONAL_TILES: readonly (TileSpec & { when: 'isVendor' | 'isCustomer' })[] = [
  // Piece 24: the Vendor Tile is built now, over VendorTaxProfile and the year's InformationReturn row.
  { key: 'vendor', label: 'Vendor', owner: 'here', draws: 'labelValueGrid', span: 1, when: 'isVendor', collapsible: true },
  { key: 'billing', label: 'Billing', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editRecord', when: 'isCustomer', collapsible: true },
  /**
   * 🔒 Piece 29, decision 2080 (T1264): the customer's Field Service tile, its
   * customer message rules set by the office. It reads its own route, and a
   * reader with no permission on the work records is served no tile.
   */
  { key: 'fieldService', label: 'Field Service', owner: 'here', draws: 'fullWidthRow', span: 2, when: 'isCustomer', collapsible: true },
];

/** 🔨 On our own Company the Contacts tile reads Employees, derived and not a flag. */
export function contactsTileLabel(isOwnCompany: boolean): string {
  return isOwnCompany ? 'Employees' : 'Contacts';
}

/**
 * 🔨 The tile's action on our own Company is + Hire, and + Add Contact is hidden rather than disabled.
 * + Hire carries the Hire window's route (decisions 2690 and 3181); + Add Contact opens the window on the page.
 */
export function contactsTileAction(isOwnCompany: boolean): { label: string; hireRoute: string | null } {
  return isOwnCompany ? { label: TILE_ACTION_LABELS.hire, hireRoute: HIRE_WINDOW_ROUTE } : { label: TILE_ACTION_LABELS.addEmployed, hireRoute: null };
}

/**
 * The Title Banner's own pills for a record: its status, and on a Company the
 * classifications it holds (the figure draws CUSTOMER beside ACTIVE). A pill
 * here is the record's own state and never an action.
 */
export interface BannerPill {
  text: string;
  group: 'good' | 'warning' | 'error' | 'inactive';
}

/** Stage n of 6: the ladder has six rungs since Discovery joined, Lost off it (piece 19; decision 5450). */
export const PROGRESS_STAGE_COUNT = 6;

/*
 * 💰 `bannerKindPills` stood here until 2026-09-22 (T175). It drew a Company's
 * classifications and a Person's employment badge as a second group of small
 * pills beside the name, above a kind line that read Company or Person and said
 * nothing. The classifications ARE the kind line now, and `pageKindLine.ts`
 * answers both — including the Retired and Undefined cases neither of those two
 * branches had.
 */

/** A fact the banner draws under the subtitle (decision 0335): a label, a value, and a route where the value is a record. */
export interface BannerFact {
  label: string;
  value: string | null;
  route?: string | null;
  /** 🔒 The value is a formatted money amount, so a negative figure in it reads red (decision 2155). */
  money?: boolean;
}

export function bannerPills(recordType: PagedRecord, facts: Readonly<Record<string, string | boolean | null | undefined>>): BannerPill[] {
  const pills: BannerPill[] = [];
  if (recordType === 'CompanyRecord' && facts.isOwnCompany === true) pills.push({ text: 'Our company', group: 'inactive' });
  // 🔒 Decision 10100: a Site names its type on its band, as the Item names its own.
  if (recordType === 'SiteRecord' && typeof facts.siteTypeName === 'string' && facts.siteTypeName) pills.push({ text: facts.siteTypeName, group: 'inactive' });
  if (recordType === 'Item') {
    if (typeof facts.type === 'string') pills.push({ text: facts.type, group: 'inactive' });
    if (typeof facts.status === 'string') pills.push({ text: facts.status === 'ActiveMature' ? 'Active · Mature' : facts.status, group: facts.status === 'Active' ? 'good' : facts.status === 'ActiveMature' ? 'warning' : 'inactive' });
    return pills;
  }
  if (recordType === 'Lead') {
    // Status, temperature and stage are three pills because they answer three questions (piece 20).
    if (typeof facts.status === 'string') pills.push({ text: facts.status === 'NotAFit' ? 'Not a fit' : facts.status === 'ConvertedToCompany' ? 'Converted to Company' : facts.status === 'ConvertedToDeal' ? 'Converted to Deal' : facts.status, group: facts.status === 'Held' ? 'warning' : facts.status === 'Blocked' ? 'error' : facts.status === 'NotAFit' ? 'inactive' : 'good' });
    if (typeof facts.temperature === 'string') pills.push({ text: facts.temperature, group: facts.temperature === 'Cold' ? 'inactive' : facts.temperature === 'Hot' ? 'warning' : 'good' });
    if (typeof facts.stage === 'string') pills.push({ text: facts.stage, group: 'inactive' });
    return pills;
  }
  if (recordType === 'Quote') {
    // The displayed status, Expired derived over a stored Sent, and the approval axis beside it where it says anything (piece 22).
    // 🔒 A Draft the gate holds reads Pending Approval, and a rejected one Rejected, in the status pill itself (T1195; decision 1861).
    const held = typeof facts.displayedStatus === 'string' ? heldDraftPill(facts.displayedStatus, typeof facts.approvalStatus === 'string' ? facts.approvalStatus : null) : null;
    if (held) pills.push(held);
    // 🔨 The word and the colour are the status table's (T2093); Sending waits on the host and Delivery Failed is a send somebody must act on (decision 1225).
    else if (typeof facts.displayedStatus === 'string') pills.push(documentStatusPill('QuoteStatus', facts.displayedStatus));
    if (!held && facts.approvalStatus === 'Pending') pills.push({ text: 'Held for approval', group: 'warning' });
    if (!held && facts.approvalStatus === 'Rejected') pills.push({ text: 'Rejected', group: 'error' });
    if (facts.approvalStatus === 'Approved') pills.push({ text: 'Approved', group: 'good' });
    // 🔨 The Quote's status pill is the derived one and there is no second: falling through drew the stored member beside it, so a Sent Quote past its expiry read Expired and Sent at once (found in review, 2026-09-15).
    return pills;
  }
  if (recordType === 'WorkOrder') {
    // The lifecycle status, the approval axis where it holds the job, and the tier the job was raised at (piece 22).
    if (typeof facts.status === 'string') pills.push(documentStatusPill('WorkOrderStatus', facts.status));
    if (facts.approvalStatus === 'Pending') pills.push({ text: 'Awaiting approval', group: 'warning' });
    if (facts.approvalStatus === 'Rejected') pills.push({ text: 'Rejected', group: 'error' });
    if (typeof facts.priorityTierName === 'string') pills.push({ text: facts.priorityTierName, group: facts.priorityTierName === 'Emergency' ? 'warning' : 'inactive' });
    if (facts.travelUnresolved === true) pills.push({ text: 'Travel unresolved', group: 'warning' });
    return pills;
  }
  if (recordType === 'Deal') {
    // Figure 81: Stage n / 5; a quick or emergency origin names itself; STALE at threshold, LOST off the ladder.
    // 🔨 The value is not a pill since decision 0665: it reads once, in the banner's summary column.
    // 🔒 A Lost Deal's reason stands beside its Lost pill (decision 0722), where General Information carried it.
    if (facts.stage === 'Lost') {
      pills.push({ text: 'Lost', group: 'error' });
      if (typeof facts.lostReason === 'string' && facts.lostReason) pills.push({ text: facts.lostReason, group: 'inactive' });
    } else if (typeof facts.stageOrdinal === 'string' || typeof facts.stageOrdinal === 'number') pills.push({ text: `Stage ${facts.stageOrdinal} / ${PROGRESS_STAGE_COUNT}`, group: facts.stage === 'Won' ? 'good' : 'inactive' });
    // 🔨 The origin is a kind-line pill since decision 0722 and is not drawn here as well.
    if (facts.quickApprovalHeld === true) pills.push({ text: 'Awaiting approval', group: 'warning' });
    if (typeof facts.staleText === 'string') pills.push({ text: facts.staleText, group: 'error' });
    return pills;
  }
  const status = typeof facts.status === 'string' ? facts.status : null;
  if (status) pills.push({ text: status, group: status === 'Active' ? 'good' : status === 'Deceased' || status === 'NotAFit' ? 'error' : 'inactive' });
  else if (recordType === 'LocationRecord' && typeof facts.verificationStatus === 'string') {
    pills.push({ text: facts.verificationStatus, group: facts.verificationStatus === 'Verified' ? 'good' : facts.verificationStatus === 'NeedsReview' || facts.verificationStatus === 'Estimated' ? 'warning' : 'inactive' });
  }
  return pills;
}

/**
 * 🔒 The accounting documents' own status pills, beside `bannerPills` (T176). The
 * product owner, 2026-09-22, asking about the one-off pieces the screens carry:
 * *"i would like to know more on the bypass of standard function why are the
 * cases not built in the function"*.
 *
 * 💰 Seven of these stood in seven page files, each exported from the page it
 * drew on, each with its own copy of the `pills.map` that rendered it. A pill's
 * colour and a pill's wording are the document's own answer and belong beside
 * every other record's, so a reader comparing an Invoice to a Bill is comparing
 * two readings of one rule rather than two rules.
 */
/**
 * 🔒 One document status as a banner pill, read through the shell's status
 * resolver (decision 0639), so a label an office edits in its rows shows on the
 * banner as it does on the list. A member the rows do not carry falls back to the
 * package's own spelling, which never invents a label.
 */
export function resolvedStatusPill(resolve: StatusResolver, entityType: string, statusValue: string): BannerPill {
  const row = resolve(entityType, statusValue);
  return row ? { text: row.label, group: row.colorGroup } : documentStatusPill(entityType, statusValue);
}

export function salesOrderPills(order: { status: string; approvalStatus: string; fulfillmentStatus: string }, resolve: StatusResolver): BannerPill[] {
  return [
    resolvedStatusPill(resolve, 'SalesOrderStatus', order.status),
    resolvedStatusPill(resolve, 'ApprovalStatus', order.approvalStatus),
    resolvedStatusPill(resolve, 'FulfillmentStatus', order.fulfillmentStatus),
  ];
}

export function changeOrderPills(change: { status: string; approvalStatus: string }, resolve: StatusResolver): BannerPill[] {
  return [resolvedStatusPill(resolve, 'ChangeOrderStatus', change.status), resolvedStatusPill(resolve, 'ApprovalStatus', change.approvalStatus)];
}

/** 🔨 A cancelled purchase order draws the receipt pill alone: an approval axis on a void order is a question nobody is asking. */
/**
 * 🔒 The Purchase Order's one pill (decision 1072): its linear status, read
 * through the one resolver, in place of the approval, sent and receipt pills
 * it drew until 2026-09-27.
 */
export function purchaseOrderPills(order: { status?: string | null }, resolve: StatusResolver): BannerPill[] {
  // 🔨 An answer with no status draws no pill rather than no page: a fixture or an older answer must not blank the banner (found in the landing of 2026-09-28).
  return order.status ? [resolvedStatusPill(resolve, 'PurchaseOrderStatus', order.status)] : [];
}

/**
 * 🔒 Which revision the vendor holds and which is only a draft, as one pill on
 * the Purchase Order's kind line (T135; decision 0712). An edit of a sent order
 * raises the next revision unsent, and Send sends that revision; until it goes,
 * the page says the vendor still holds the one before. A cancelled order draws
 * none.
 */
export function purchaseOrderRevisionPill(order: { revision: number; sentRevision: number | null; fulfillmentStatus: string }): BannerPill | null {
  if (order.fulfillmentStatus === 'Cancelled') return null;
  const two = (n: number): string => String(n).padStart(2, '0');
  // 🔒 Decision 1072: whether the order went is the one status's to say, so an order with no revision draws no pill here.
  if (order.revision === 0) return null;
  if (order.sentRevision === null) return { text: `${t('po.revision.draft')} ${two(order.revision)}`, group: 'inactive' };
  if (order.sentRevision >= order.revision) return { text: order.revision > 0 ? `${t('po.revision.sentRevision')} ${two(order.revision)}` : t('po.revision.sent'), group: 'good' };
  return { text: `${t('po.revision.draft')} ${two(order.revision)} · ${t('po.revision.vendorHolds')} ${two(order.sentRevision)}`, group: 'warning' };
}

/** 🔒 A bill of materials' pill is its own `BOMStatus` (decision 1091, superseding 1053's Deal stage). */
export function bomPills(bom: { status: string }, resolve: StatusResolver): BannerPill[] {
  return [resolvedStatusPill(resolve, 'BOMStatus', bom.status)];
}

/**
 * The Invoice's status, and beside it an Unpriced Commission pill while its
 * commission waits for a rate (decision 1221): a warning, because the invoice
 * issued and only the accrual is missing.
 */
export function invoicePills(invoice: { status: string; unpricedCommission?: unknown; isOpening?: boolean }, resolve: StatusResolver): BannerPill[] {
  const pills = [resolvedStatusPill(resolve, 'InvoiceStatus', invoice.status)];
  if (invoice.unpricedCommission) pills.push({ text: t('invoice.pill.unpricedCommission'), group: 'warning' });
  // 🔒 An invoice the import loaded as open at the cut-off says so beside its status (decision 2992).
  if (invoice.isOpening) pills.push({ text: t('invoice.pill.opening'), group: 'inactive' });
  return pills;
}

export function paymentPills(payment: { status: string }, resolve: StatusResolver): BannerPill[] {
  return [resolvedStatusPill(resolve, 'PaymentStatus', payment.status)];
}

export function creditNotePills(note: { status: string }, resolve: StatusResolver): BannerPill[] {
  return [resolvedStatusPill(resolve, 'CreditNoteStatus', note.status)];
}

/**
 * 🔒 The Vendor Credit's banner (decision 7580): its status from the table, and the
 * approval beside it where one is waiting or refused, in the Bill's own words.
 */
export function vendorCreditPills(credit: { status: string; approvalStatus: string }, resolve: StatusResolver): BannerPill[] {
  const pills: BannerPill[] = [resolvedStatusPill(resolve, 'VendorCreditStatus', credit.status)];
  if (credit.approvalStatus === 'Pending') pills.push({ text: t('bill.approval.pending'), group: 'warning' });
  if (credit.approvalStatus === 'Rejected') pills.push({ text: t('bill.approval.rejected'), group: 'error' });
  return pills;
}

/**
 * The Bill's two axes and the variance flag beside them (decision 0412). 🔨 The
 * flag is its own pill in the error group: it is a variance nobody has answered,
 * not a state of the bill's own lifecycle.
 */
export function billPills(bill: { status: string; approvalStatus: string; varianceFlagged: boolean; isOpening?: boolean }): BannerPill[] {
  if (bill.status === 'Void') return [{ text: t('bill.pill.void'), group: 'inactive' }];
  // 🔨 The status pill reads the table (T2093), so a part-paid bill reads Partially Paid, never the member's own spelling.
  const pills: BannerPill[] = [documentStatusPill('BillStatus', bill.status)];
  if (bill.approvalStatus === 'Pending') pills.push({ text: t('bill.approval.pending'), group: 'warning' });
  if (bill.approvalStatus === 'Rejected') pills.push({ text: t('bill.approval.rejected'), group: 'error' });
  if (bill.approvalStatus === 'Approved') pills.push({ text: t('bill.approval.approved'), group: 'good' });
  if (bill.varianceFlagged) pills.push({ text: t('bill.pill.flagged'), group: 'error' });
  // 🔒 A bill the import loaded as open at the cut-off says so beside its status (decision 2992).
  if (bill.isOpening) pills.push({ text: t('bill.pill.opening'), group: 'inactive' });
  return pills;
}

export function companyTiles(facts: { isOwnCompany: boolean; isCustomer: boolean; isVendor: boolean; seesWork?: boolean; seesAssets?: boolean; ownsCompanies?: boolean }): TileSpec[] {
  // 🔒 Account standing is a customer's tile (decision 2020).
  // 🔒 Subsidiaries draws only on a company that owns or has owned another (decision 7501).
  // 🔨 A reader with no permission on the work records is served no Work tile, not an empty one (piece 22), and the same holds for the asset records (piece 28).
  const base = COMPANY_TILES.filter((tile) => !(tile.notOnOwnCompany && facts.isOwnCompany) && !(tile.key === 'accountStanding' && !facts.isCustomer) && !(tile.key === 'work' && facts.seesWork === false) && !(tile.key === 'assets' && facts.seesAssets === false) && !(tile.key === 'subsidiaries' && facts.ownsCompanies !== true));
  const conditional = COMPANY_CONDITIONAL_TILES.filter((tile) => (tile.when === 'isVendor' ? facts.isVendor : facts.isCustomer) && !(tile.key === 'fieldService' && facts.seesWork === false));
  return [...base, ...conditional];
}

/** 🔒 The Site record page's locked order. */
export const SITE_TILES: readonly TileSpec[] = [
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 2, action: 'editRecord', collapsible: true },
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editCustomFields', collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'roles', label: 'Roles', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addSiteRole', collapsible: true },
  { key: 'requirements', label: 'Requirements & PPE', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addRequirement', collapsible: true },
  /** 🔒 Decision 0739: the jurisdictions the address resolves to, the coverage, the warnings and the exemption forms the site needs; the panel draws itself. */
  { key: 'taxJurisdiction', label: 'Tax Jurisdiction', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'certificates', label: 'Tax Exempt Certificates', owner: 'here', draws: 'certificateCard', span: 1, action: 'addCertificate', collapsible: true },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 2, action: 'addNote', collapsible: true },
  { key: 'siteFiles', label: 'Site Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'upcomingEvents', label: 'Upcoming Events', owner: 'here', draws: 'eventBooking', span: 2, action: 'addEvent', collapsible: true },
  { key: 'checkIns', label: 'Check-Ins', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'checkIn', collapsible: true },
  { key: 'stock', label: 'Stock', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  /** Piece 22: the WorkOrder rows performed at this site, newest first. */
  { key: 'work', label: 'Work', owner: 'here', draws: 'fullWidthRow', span: 2, collapsible: true },
  /** 🔒 Decision 12540: the trees standing at this site, drawn by the asset register's own tree with the node page beside it, across the canvas. */
  { key: 'assets', label: 'Assets', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  /** 🔒 Decision 12503: the plant feed's connector, its state, its outages and its tag maps; the panel draws itself, for a holder of the asset register's write. */
  { key: 'plantFeed', label: 'Plant Feed', owner: 'here', draws: 'fullWidthRow', span: 2, collapsible: true },
];

/** The two tax tiles of the Site page, which only a reader of the Accounting module is served (decision 1633). */
export const SITE_TAX_TILE_KEYS: readonly string[] = ['taxJurisdiction', 'certificates'];

/**
 * The Site page's tiles; the Work tile is absent for a reader with no permission on the work records (piece 22).
 * 🔒 The two tax tiles are absent for a reader who does not read the Accounting module, and the certificate request links under them go with them (decision 1633).
 * 🔒 The Assets tile is absent for a reader who does not read the asset records (decision 12540).
 */
export function siteTiles(facts: { seesWork?: boolean; seesTax?: boolean; seesAssets?: boolean; seesPlantFeed?: boolean } = {}): TileSpec[] {
  // 🔒 Decision 12503: the Plant Feed tile is served to a holder of the asset register's write alone, since every act on it writes.
  return SITE_TILES.filter((tile) => !(tile.key === 'work' && facts.seesWork === false) && !(SITE_TAX_TILE_KEYS.includes(tile.key) && facts.seesTax === false) && !(tile.key === 'assets' && facts.seesAssets === false) && !(tile.key === 'plantFeed' && facts.seesPlantFeed === false));
}

/**
 * The Location page shows the building's own facts and never a tenant's.
 * 🔒 Upcoming Events joins the canvas with T167 (decision 0654): the tile's
 * own query already widened to read an event placed at the building or at
 * any Site standing on it, and until this entry existed nothing on the page
 * ever drew what that query answered.
 */
export const LOCATION_TILES: readonly TileSpec[] = [
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 2, action: 'editRecord', collapsible: true },
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editCustomFields', collapsible: true },
  { key: 'occupants', label: 'Occupants', owner: 'here', draws: 'location', span: 2, action: 'addOccupant', collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 2, action: 'addNote', collapsible: true },
  { key: 'locationFiles', label: 'Location Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  { key: 'upcomingEvents', label: 'Upcoming Events', owner: 'here', draws: 'eventBooking', span: 2, action: 'addEvent', collapsible: true },
];

/**
 * The Person page. 🔒 It carries no HR tile and no Show HR Details control
 * (decision 3180): Trainings and Certifications, Clock Credential History,
 * Compensation, Time, Custody and the PTO, Discipline, Separation and Active
 * Clock Credential slots are sections of the Employee page in HR, one per job.
 * The page keeps the name, the contact details, the skills, the files, the
 * custom fields and the Employments tile, whose row at our own Company opens
 * that job's Employee page for a holder of the HR pages permission.
 */
export const PERSON_TILES: readonly TileSpec[] = [
  /**
   * 🔒 General Information takes the first slot again (T170), carrying the
   * nickname the banner used to carry as a fact. The title bar standard carries
   * no facts row, and the nickname has nowhere else on the page to read.
   * 💰 Decision 0335 dropped the tile because the banner held the fact; the
   * Employments tile already says the title and the company in full, and the
   * Contact details tile says every open line.
   */
  { key: 'generalInformation', label: 'General Information', owner: 'here', draws: 'labelValueGrid', span: 1, collapsible: true },
  { key: 'customFields', label: 'Custom Fields', owner: 'here', draws: 'labelValueGrid', span: 1, action: 'editCustomFields', collapsible: true },
  /** One tile: every position the person has held, dated, grouped under the employment it belongs to (the product owner, 2026-09-09: the two were the same thing). */
  { key: 'employments', label: 'Employments', owner: 'here', draws: 'jobTimeline', span: 1, action: 'addEmployment', collapsible: true },
  { key: 'contactMethods', label: 'Contact Details', owner: 'here', draws: 'contactRow', span: 1, action: 'addContactMethod', collapsible: true },
  { key: 'flags', label: 'Flags', owner: 'here', draws: 'statusOnly', span: 1, action: 'addTag', placement: 'banner' },
  { key: 'internalNotes', label: 'Internal Notes', owner: 'here', draws: 'note', span: 2, action: 'addNote', collapsible: true },
  /** 🔒 Decision 5210: the Person's texts on the company number, under Internal Notes, read from their own route with a box and Send at the foot. */
  { key: 'texts', label: 'Texts', owner: 'here', draws: 'fullWidthRow', span: 2, collapsible: true },
  { key: 'personFiles', label: 'Person Files', owner: 'here', draws: 'file', span: 1, action: 'upload', pageSize: FILES_PAGE_SIZE, collapsible: true },
  /** 🔒 T2352 (piece 13): each confidentiality role granted to the person, dated, with its backing NDA's state, read from its own route. */
  { key: 'confidentialityRoles', label: 'Confidentiality Roles', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'grantConfidentialityRole', collapsible: true },
  { key: 'upcomingEvents', label: 'Upcoming Events', owner: 'here', draws: 'eventBooking', span: 2, action: 'addEvent', collapsible: true },
  { key: 'accessInvitations', label: 'Access Invitations', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  /** Piece 22, decisions 0464 and 0614: what this person is recorded as able to do — each skill held whole, and the sub-skills beneath the skills they work in. */
  { key: 'skills', label: 'Skills', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addSkill', collapsible: true },
  /** 🔒 Decision 6180: the schools a person attended, a list like the employments; the skills a check found outside the catalog; and the LinkedIn page and assumed values. Each reads its own route. */
  /** 🔒 Decision 6400: + Add School in the tile head, and the pencil and the remove on each school's row, as the Employments tile draws them. */
  { key: 'education', label: 'Education', owner: 'here', draws: 'fullWidthRow', span: 1, action: 'addEducation', collapsible: true },
  { key: 'otherSkills', label: 'Other Skills', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'profile', label: 'Profile', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  /** Piece 18: the person's DealContactLink and DealTeamMember rows, the share withheld by the field rule (decision 0125). */
  { key: 'dealLinks', label: 'Deals', owner: 'here', draws: 'fullWidthRow', span: 3, collapsible: true },
  /** 🔒 Decision 5382: each undecided Differs mark of a validation check, newest first, read from its own route with Accept and Dismiss. */
  { key: 'suggestions', label: 'Suggestions', owner: 'here', draws: 'fullWidthRow', span: 1, collapsible: true },
  { key: 'history', label: 'History', owner: 'here', draws: 'timeline', span: 3, collapsible: true, windowRows: HISTORY_WINDOW_ROWS },
];

/**
 * A person with an ended employment at our own Company and an active one
 * elsewhere carries a Former employee badge, derived from the employments and
 * never stored.
 */
export function formerEmployee(
  employments: readonly { companyId: string; endDate?: Date | null }[],
  organizationCompanyId: string,
  day: string,
): boolean {
  // 🔒 In force through the end date, the organization's day handed in by the caller (decision 2860).
  const open = (row: { endDate?: Date | null }) => inForceThrough(row.endDate, day);
  const endedHere = employments.some((e) => e.companyId === organizationCompanyId && !open(e));
  const activeElsewhere = employments.some((e) => e.companyId !== organizationCompanyId && open(e));
  return endedHere && activeElsewhere;
}

/**
 * The top of the Person page shows the most recently started active employment;
 * every other one sits in the Employments tile. Nothing stores a primary flag.
 */
export function headlineEmployment<T extends { startDate: Date; endDate?: Date | null }>(employments: readonly T[], day: string): T | null {
  // 🔒 An employment ending today still heads the page today, as the Employments tile keeps its Current pill (decision 2860).
  const active = employments.filter((e) => inForceThrough(e.endDate, day));
  return [...active].sort((a, b) => b.startDate.getTime() - a.startDate.getTime())[0] ?? null;
}

/**
 * 🔨 On our own Company the two-step modal refuses to create an employment
 * (decision 0165 amending 0138): pre-filling the company there would create an
 * internal employment and bypass the domain interlock, the hire screen's own
 * fields and the ADP new-workers queue.
 */
export interface ModalRefusal {
  refused: boolean;
  /** 🔨 The Company is never silently filtered out: a row in its place says so. */
  reason: string | null;
  /** Where the refused hire is made instead: the Hire window (decisions 2690 and 3181). */
  hireRoute: string | null;
}

/** The edit form's own title, composed where the strings live rather than in the screen. */
export function editTitle(singular: string): string {
  return `Edit ${singular}`;
}

/** 🔨 The refusal carries the Hire window's route, which is where our own Company's jobs are made (decisions 2690 and 3181). */
export function employmentModalRefused(companyId: string, organizationCompanyId: string | null): ModalRefusal {
  if (organizationCompanyId === null || companyId !== organizationCompanyId) return { refused: false, reason: null, hireRoute: null };
  return { refused: true, reason: 'somebody employed here is hired through HR, where the department, work location and jurisdiction are collected', hireRoute: HIRE_WINDOW_ROUTE };
}

/** The tiles a builder renders now; the rest arrive with the pieces that declare their records. */
export function tilesBuiltHere(tiles: readonly TileSpec[]): TileSpec[] {
  return tiles.filter((tile) => tile.owner === 'here');
}

/**
 * The fields each record page's own form edits (piece 13). 🔨 A field absent
 * here is not editable from the page: a custom field is written through its own
 * tile, a tag through the Flags tile, and a dated link is closed and opened
 * rather than edited in place.
 */
/**
 * A name, an email, a phone and a postal code are their own kinds (decision
 * 0290): the form title-cases a name and lower-cases an email on blur, draws a
 * phone through the Formatted Input's mask and checks a postal code's shape,
 * and the API applies the same rules again on the write.
 *
 * 🔒 `multiline` is a paragraph (T87; decision 0685): piece 3's Textarea, two
 * lines tall and growing as it is typed into, its line breaks kept. It is the
 * kind for notes, descriptions and reasons, which piece 3 says type into one.
 */
/** 🔒 `sealed` is a number held encrypted, an EIN or a DUNS number (decision 3082): typed on a create, and on an edit read as on file or not beside a Change control, never shown. */
export type EditFieldKind = 'text' | 'multiline' | 'boolean' | 'lines' | 'number' | 'select' | 'radio' | 'company' | 'site' | 'date' | 'name' | 'email' | 'phone' | 'postalCode' | 'county' | 'sealed';

/** Where a closed list's options come from: a fixed set, or one of the page's own reference reads. */
/** 🔒 `siteTypes` is the managed site type list (decision 10100), read from its own route rather than the Item form's options. */
export type EditOptionSource = 'itemCategories' | 'saleUnits' | 'taxCategories' | 'items' | 'dealTypes' | 'siteTypes';

export interface EditField {
  key: string;
  label: string;
  kind: EditFieldKind;
  required?: boolean;
  /**
   * A fixed option list for a select or a radio. 🔒 An option drawn only while another
   * field holds one of some values names them in `shownWhen`, as a Deal's Service
   * Request route is offered on a Service deal alone (decision 1912).
   */
  options?: readonly { value: string; label: string; shownWhen?: { key: string; oneOf: readonly string[] }; editOnly?: boolean }[];
  /** A reference list the form reads once for a select. */
  optionsFrom?: EditOptionSource;
  /** True where the field belongs to a Good and hides when the Item is a Service (figure 107). */
  goodsOnly?: boolean;
  /** A number field's units (decision 0297): a currency edits in dollars and stores dollars here, a percent draws its sign, a count draws bare. Absent means a bare number. */
  numeric?: 'currency' | 'percent' | 'number';
  /** As wide as this many characters where the answer's length is known (decision 0297). */
  chars?: number;
  /** A number field's decimal places where the answer is a whole count, such as a threshold in days (decision 0669). */
  decimals?: number;
  /** True where the field is chosen once at creation and the Edit form never offers it, such as a Deal's origin (decision 0353). */
  createOnly?: boolean;
  /**
   * True where the Edit form alone offers the field (decision 0669): the Company's
   * settings a quick-create does not ask for, and a default billing site, which has
   * no sites to offer before the company exists.
   */
  editOnly?: boolean;
  /** A small heading drawn above the field, opening a group of the fields after it, such as the Company's Billing (decision 0669). */
  heading?: string;
  /** The value a create form starts on where the field is a choice; sent when nothing else was chosen. */
  defaultValue?: string;
  /** The value a create form starts on follows another field's choice, such as a Deal's Origin following its Type (decision 10740); a choice not named falls back to `defaultValue`. */
  defaultFrom?: { key: string; byValue: Readonly<Record<string, string>> };
  /** The field is absent while another field holds one of these values, such as a Deal's Value on a quick route (decision 0353). */
  hiddenWhen?: { key: string; oneOf: readonly string[] };
  /**
   * 🔒 The flag a company field's choice must carry, read off the kind's own
   * fact (T184; decision 0637). The picker asks the list for that side alone;
   * absent offers every company, which is what a Manufacturer field wants.
   */
  counterparty?: Counterparty;
  /**
   * 🔒 The field whose value this one's choices come from, and whose change
   * empties this one (decision 0737): a Location's County picks from its
   * state's counties, and changing the state resets it.
   */
  dependsOn?: string;
}

/**
 * The fields a form draws: every field on a create, the create-only ones left
 * off an edit, and a field hidden by another's value absent on both. The
 * hidden test reads the default where nothing was chosen yet.
 */
/**
 * 🔒 The options a choice field draws for the form's values as they stand
 * (decision 1912): an option naming `shownWhen` stands only while that field holds
 * one of its values. A hidden option is never sent, because the form drops a held
 * value its options no longer offer. An option marked `editOnly` stands on the Edit
 * form alone, as a person's Deceased does (T2425).
 */
export function shownOptions(field: EditField, read: (key: string) => unknown, mode: 'create' | 'edit' = 'edit'): readonly { value: string; label: string }[] {
  return (field.options ?? []).filter((option) => !(mode === 'create' && option.editOnly)).filter((option) => !option.shownWhen || option.shownWhen.oneOf.includes(String(read(option.shownWhen.key) ?? '')));
}

/** The value a field starts on, its `defaultFrom` choice read off the form's values first, then its `defaultValue` (decision 10740). */
export function fieldDefault(field: EditField, read: (key: string) => unknown): string | undefined {
  if (field.defaultFrom) {
    const from = field.defaultFrom.byValue[String(read(field.defaultFrom.key) ?? '')];
    if (from !== undefined) return from;
  }
  return field.defaultValue;
}

export function visibleEditFields(recordType: PagedRecord, mode: 'create' | 'edit', read: (key: string) => unknown): EditField[] {
  const fields = EDIT_FIELDS[recordType];
  const valueOf = (key: string): unknown => {
    const held = read(key);
    if (held !== undefined && held !== null && held !== '') return held;
    const field = fields.find((f) => f.key === key);
    return field ? fieldDefault(field, read) : undefined;
  };
  return fields.filter((field) => !(mode === 'edit' && field.createOnly) && !(mode === 'create' && field.editOnly)).filter((field) => !(field.hiddenWhen && field.hiddenWhen.oneOf.includes(String(valueOf(field.hiddenWhen.key) ?? ''))))
    // 🔒 An edit draws a sealed number only where the page read says whether one is on file, which it says to a viewer holding the Accounting class alone (decision 3082).
    .filter((field) => !(mode === 'edit' && field.kind === 'sealed' && typeof read(`${field.key}OnFile`) !== 'boolean'));
}

/** A Company's `status`, the `CompanyStatus` lookup, as the form offers it (piece 10). */
export const COMPANY_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'Active', label: 'Active' },
  { value: 'BoughtOut', label: 'Bought Out' },
  { value: 'OutOfBusiness', label: 'Out of Business' },
];

/**
 * A customer's `invoiceFilePolicy` in words (decision 0669), read by the form's
 * choice and by the Billing Tile, so the two say the same thing.
 */
export const INVOICE_FILE_POLICY_OPTIONS: readonly { value: 'OnePerFile' | 'MultiplePerFile'; label: string }[] = [
  { value: 'OnePerFile', label: 'One invoice per file' },
  { value: 'MultiplePerFile', label: 'Several invoices in one file' },
];

export function invoiceFilePolicyLabel(value: string): string {
  return INVOICE_FILE_POLICY_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export const EDIT_FIELDS: Readonly<Record<PagedRecord, readonly EditField[]>> = {
  // 🔨 Piece 22: a Sales Order, an Invoice, a Credit Note and a Payment are raised from their
  // own flow — a conversion, a Sales Order, an invoice line or a Record Payment — never from a
  // field grid, so each declares no quick-create form.
  SalesOrder: [],
  Invoice: [],
  CreditNote: [],
  Payment: [],
  // 🔨 An RFQ is raised from a Deal's own RFQ frame, where the lines it asks about already
  // stand, and a BOM is built on the Deal's page. Neither is typed into a field grid, so
  // neither declares a quick-create form (decision 0488).
  RFQ: [],
  BOM: [],
  /**
   * 🔒 The Company form carries every setting on the record a person answers
   * (decision 0669; the owner's T86). The fields marked edit-only are the Edit
   * form's: the quick-create keeps its short list.
   */
  CompanyRecord: [
    { key: 'displayName', label: 'Display name', kind: 'text', required: true },
    { key: 'status', label: 'Status', kind: 'select', editOnly: true, options: COMPANY_STATUS_OPTIONS },
    // 🔒 Decision 7690: Bought Out names the company that took this one over, an existing one or a new name typed, and the two fields stand only while Bought Out is chosen.
    { key: 'successorCompanyId', label: 'Bought out by', kind: 'company', editOnly: true, hiddenWhen: { key: 'status', oneOf: ['Active', 'OutOfBusiness', ''] } },
    { key: 'successorName', label: 'Or a new company named', kind: 'text', editOnly: true, hiddenWhen: { key: 'status', oneOf: ['Active', 'OutOfBusiness', ''] } },
    // 🔨 No payment terms here, on the quick-create or the Edit form: they are the Account standing tile's, under the Accounting class (T361, T364).
    { key: 'website', label: 'Website', kind: 'text' },
    { key: 'linkedInUrl', label: 'LinkedIn', kind: 'text', editOnly: true },
    // 🔒 Decision 6180: the industry, the size and the founding year a validation check suggests, edited and cleared here.
    { key: 'industry', label: 'Industry', kind: 'text', editOnly: true },
    { key: 'employeeRange', label: 'Size (people)', kind: 'text', editOnly: true },
    // A year, typed as text so no digit grouping is drawn into it; the save reads it as a whole year.
    { key: 'foundedYear', label: 'Founded', kind: 'text', editOnly: true },
    { key: 'mainPhoneNumber', label: 'Main phone', kind: 'phone' },
    // 🔒 Decision 3082 (T2246): the two sealed numbers, written through the company service's encrypted write and never shown.
    { key: 'ein', label: 'Tax ID', kind: 'sealed' },
    { key: 'dunsNumber', label: 'DUNS number', kind: 'sealed' },
    { key: 'isCustomer', label: 'Customer', kind: 'boolean' },
    { key: 'isVendor', label: 'Vendor', kind: 'boolean' },
    { key: 'isManufacturer', label: 'Manufacturer', kind: 'boolean' },
    // The Company's own step of the follow-up chain, between a tag's and the Organization's (decision 0209).
    { key: 'followUpThresholdDays', label: 'Follow-up threshold (days)', kind: 'number', chars: 14, decimals: 0, editOnly: true },
    { key: 'financeChargeExempt', label: 'Exempt from finance charges', kind: 'boolean', editOnly: true },
    { key: 'financeChargeExemptReason', label: 'Why it is exempt', kind: 'multiline', editOnly: true, hiddenWhen: { key: 'financeChargeExempt', oneOf: ['false', ''] } },
    { key: 'defaultBillingSiteId', label: 'Default billing site', kind: 'site', editOnly: true, heading: 'Billing' },
    { key: 'requiresPoNumber', label: 'Requires a PO number', kind: 'boolean' },
    { key: 'invoiceFilePolicy', label: 'Invoice file policy', kind: 'radio', editOnly: true, defaultValue: 'OnePerFile', options: INVOICE_FILE_POLICY_OPTIONS },
    { key: 'allowsArchiveAttachments', label: 'Accepts archive attachments', kind: 'boolean', editOnly: true },
    { key: 'invoiceSubmissionNotes', label: 'Invoice submission notes', kind: 'multiline' },
  ],
  PersonRecord: [
    { key: 'firstName', label: 'First name', kind: 'name', required: true },
    { key: 'lastName', label: 'Last name', kind: 'name', required: true },
    { key: 'nickname', label: 'Nickname', kind: 'name' },
    // 🔨 Only Deceased gates anything (decision 0169); the form confirms that one move with its counts before the save, and a new person is never offered it (T2425).
    { key: 'status', label: 'Status', kind: 'select', options: [{ value: 'Active', label: 'Active' }, { value: 'Retired', label: 'Retired' }, { value: 'Deceased', label: 'Deceased', editOnly: true }] },
  ],
  LocationRecord: [
    // 🔒 An address or a building name, not both (decision 0673): a field, a yard or a tower has a name and no street.
    { key: 'addressLines', label: 'Address', kind: 'lines' },
    { key: 'locality', label: 'City', kind: 'text' },
    { key: 'administrativeArea', label: 'State', kind: 'text' },
    // 🔒 A pick from the Census Bureau's counties for the location's state, reset when the state changes (decision 0737).
    { key: 'county', label: 'County', kind: 'county', dependsOn: 'administrativeArea' },
    { key: 'postalCode', label: 'Postal code', kind: 'postalCode' },
    { key: 'buildingName', label: 'Building name', kind: 'text' },
    { key: 'phoneNumber', label: 'Phone', kind: 'phone' },
  ],
  SiteRecord: [
    { key: 'suite', label: 'Suite', kind: 'text' },
    { key: 'name', label: 'Site name', kind: 'text' },
    // 🔒 Decision 10100 (T3476): what kind of place the site is, from the active types; never required, and a retired type a site already carries stays chosen.
    { key: 'siteTypeId', label: 'Site type', kind: 'select', optionsFrom: 'siteTypes' },
    { key: 'gateSignInEnabled', label: 'Gate sign-in', kind: 'boolean' },
    // 🔒 Decision 10070 (T3457): the certificate check reads only a site carrying this flag.
    { key: 'isTaxExempt', label: 'Tax exempt customer', kind: 'boolean' },
  ],
  // Piece 17, figure 107: the catalog fields, Type as a radio, the Goods-only block shown only for a Good.
  Item: [
    { key: 'name', label: 'Name', kind: 'text', required: true },
    { key: 'sku', label: 'Part number', kind: 'text', required: true },
    { key: 'type', label: 'Type', kind: 'radio', required: true, options: [{ value: 'Good', label: 'Good' }, { value: 'Service', label: 'Service' }] },
    { key: 'categoryId', label: 'Category', kind: 'select', optionsFrom: 'itemCategories' },
    { key: 'manufacturerCompanyId', label: 'Manufacturer', kind: 'company', goodsOnly: true },
    { key: 'manufacturerPartNumber', label: 'Manufacturer part number', kind: 'text', goodsOnly: true },
    { key: 'manufacturerReferenceName', label: 'Reference name', kind: 'text', goodsOnly: true },
    { key: 'series', label: 'Series', kind: 'text', goodsOnly: true },
    { key: 'unitId', label: 'Unit of sale', kind: 'select', optionsFrom: 'saleUnits', required: true },
    { key: 'taxCategoryId', label: 'Tax category', kind: 'select', optionsFrom: 'taxCategories' },
    { key: 'salesDescription', label: 'Sales description', kind: 'multiline' },
    { key: 'purchaseDescription', label: 'Purchase description', kind: 'multiline' },
    { key: 'listPrice', label: 'List price', kind: 'number', numeric: 'currency', chars: 12 },
    { key: 'purchasePrice', label: 'Purchase price', kind: 'number', numeric: 'currency', chars: 12 },
    { key: 'salesPrice', label: 'Sales price', kind: 'number', numeric: 'currency', chars: 12 },
    { key: 'defaultMargin', label: 'Default margin %', kind: 'number', numeric: 'percent', chars: 8 },
    { key: 'minimumMargin', label: 'Minimum margin %', kind: 'number', numeric: 'percent', chars: 8 },
    { key: 'minimumSaleQuantity', label: 'Minimum sale quantity', kind: 'number', chars: 8 },
    { key: 'saleQuantityIncrement', label: 'Sale quantity increment', kind: 'number', chars: 8 },
    { key: 'returnableDefault', label: 'Returnable', kind: 'boolean', goodsOnly: true },
    // 🔨 Piece 23: the flag records only that units carry serials worth capturing at install; it does not switch inventory into unit-level tracking, and the label says so.
    { key: 'isSerialized', label: 'Is serialized — serials captured at install, not at receiving', kind: 'boolean', goodsOnly: true },
    { key: 'status', label: 'Status', kind: 'select', options: [{ value: 'Active', label: 'Active' }, { value: 'ActiveMature', label: 'Active, mature' }, { value: 'Discontinued', label: 'Discontinued' }, { value: 'Inactive', label: 'Inactive' }] },
    { key: 'replacedByItemId', label: 'Replaced by', kind: 'select', optionsFrom: 'items' },
  ],
  /**
   * The Deal form (piece 18): Name, Account, Type, Value and the three expected dates.
   * 🔨 Stage is not typed: Intent, Evaluation and Negotiation end on the page's
   * own Mark Complete button through canCompleteStage, and Won and Lost on
   * their own actions (decision 0206). Contacts are dated rows on their tile,
   * closed and opened rather than edited (decision 0076).
   */
  Deal: [
    { key: 'name', label: 'Name', kind: 'text', required: true },
    { key: 'companyId', label: 'Account', kind: 'company', required: true, counterparty: COUNTERPARTY.Deal },
    { key: 'dealType', label: 'Type', kind: 'radio', required: true, options: [{ value: 'Project', label: 'Project' }, { value: 'Parts', label: 'Parts' }, { value: 'Service', label: 'Service' }] },
    // 🔨 The origin is chosen once and never edited; a quick route opens at Won and the Quote total writes its value (decision 0353).
    // 🔒 A Service deal offers a third route, Service Request, beside the two it already offers (decision 1912).
    // 🔨 Never Emergency: the Service form's Emergency switch books an emergency with its job (decision 2294; T1733).
    { key: 'origin', label: 'Origin', kind: 'radio', createOnly: true, defaultValue: 'Pipelined', defaultFrom: { key: 'dealType', byValue: { Project: defaultOrigin('Project'), Parts: defaultOrigin('Parts'), Service: defaultOrigin('Service') } }, options: [{ value: 'Pipelined', label: 'Pipeline' }, { value: 'QuickBooked', label: 'Quick Deal' }, { value: 'ServiceRequest', label: 'Service Request', shownWhen: { key: 'dealType', oneOf: ['Service'] } }] },
    { key: 'dealValue', label: 'Value', kind: 'number', numeric: 'currency', chars: 12, hiddenWhen: { key: 'origin', oneOf: ['QuickBooked', 'Emergency', 'ServiceRequest'] } },
    // 🔒 Asked once, on the New Deal form; from then on the Intent card's Expected quote writes it (decision 0719), so the Edit form does not offer it.
    // 🔒 A request asks its preferred window instead, and has no Intent card to write a date from (decision 1912).
    // 🔒 Three dates under their own names (decision 10741; T1016): the Expected quote alone is required, and Expected close and Expected finish are optional. The Intent card's Timing row keeps the quote and the finish from then on; the Expected close stays on the Edit form.
    { key: 'expectedQuoteDate', label: 'Expected quote', kind: 'date', required: true, createOnly: true, hiddenWhen: { key: 'origin', oneOf: ['QuickBooked', 'Emergency', 'ServiceRequest'] } },
    { key: 'expectedCloseDate', label: 'Expected close', kind: 'date', hiddenWhen: { key: 'origin', oneOf: ['QuickBooked', 'Emergency', 'ServiceRequest'] } },
    { key: 'expectedFinishDate', label: 'Expected finish', kind: 'date', createOnly: true, hiddenWhen: { key: 'origin', oneOf: ['QuickBooked', 'Emergency', 'ServiceRequest'] } },
  ],
  /**
   * The Quote form (piece 22): the Account once at creation, the dates, the
   * terms snapshotted from the Company and the display mode. The Bill To and
   * Ship To are chosen in the New Quote modal from the Account's own sites and
   * edited on the page; the lines are the Item Table's, never a field here.
   */
  Quote: [
    { key: 'companyId', label: 'Account', kind: 'company', required: true, createOnly: true, counterparty: COUNTERPARTY.Quote },
    { key: 'quoteDate', label: 'Quote date', kind: 'date' },
    { key: 'expiryDate', label: 'Good till', kind: 'date' },
    { key: 'paymentTerms', label: 'Payment terms', kind: 'text' },
    { key: 'displayMode', label: 'Display mode', kind: 'radio', defaultValue: 'Parts', options: [{ value: 'Parts', label: 'Parts' }, { value: 'TimeAndMaterial', label: 'Time & Material' }, { value: 'Project', label: 'Project' }] },
  ],
  /** 🔨 A Work Order is never edited through the generic form: its lines, workers, travel and status each have their own write (piece 22). */
  WorkOrder: [],
  /** 🔨 A Service Report is never edited through the generic form: it is worked on its visit's page (decision 2910). */
  ServiceReport: [],
  /** The Lead form (piece 20): the source and the occasion, and the captured contact as it arrived; a link never overwrites the submitted text. */
  Lead: [
    { key: 'source', label: 'Source', kind: 'radio', required: true, options: [{ value: 'ManualEntry', label: 'Manual entry' }, { value: 'TextMessage', label: 'Text message' }, { value: 'Website', label: 'Website' }] },
    { key: 'sourceDetail', label: 'Occasion', kind: 'text' },
    { key: 'submittedName', label: 'Name', kind: 'name', required: true },
    { key: 'submittedCompanyName', label: 'Company', kind: 'text' },
    { key: 'submittedEmail', label: 'Email', kind: 'email' },
    { key: 'submittedPhone', label: 'Phone', kind: 'phone' },
    { key: 'submittedAddress', label: 'Address', kind: 'multiline' },
    { key: 'message', label: 'Message', kind: 'multiline' },
  ],
  // Piece 22: a Purchase Order is created and edited on its own document page (decision 0409); the generic form carries nothing for it.
  PurchaseOrder: [],
  // Piece 22, the purchase and stock side: each is created through its own act — a bill's lines, a credit's amount, a count's site, a
  // transfer's two ends, a revaluation's two costs — so the generic form carries nothing for any of the five (built 2026-09-15).
  Bill: [],
  VendorCredit: [],
  StockCount: [],
  StockTransfer: [],
  StockRevaluation: [],
  // Decision 0761: a journal is written on its own form and posted as it is saved, never through the field modal.
  JournalEntry: [],
};

/** 🔨 A required field left blank blocks the save and names itself. */
export function editProblems(recordType: PagedRecord, values: Readonly<Record<string, unknown>>): string[] {
  const own = recordType === 'Item' ? saleQuantityProblems(numberOrNull(values.minimumSaleQuantity), numberOrNull(values.saleQuantityIncrement)) : recordType === 'LocationRecord' ? locationNameProblems(values) : [];
  return own.concat(EDIT_FIELDS[recordType]
    .filter((field) => field.required)
    // 🔒 A required field the form did not draw (hidden by another's value, or kept to the create or the edit form) is not missing from the save (decision 10741).
    .filter((field) => !((field.hiddenWhen || field.createOnly || field.editOnly) && !(field.key in values)))
    .filter((field) => {
      const value = values[field.key];
      if (Array.isArray(value)) return value.filter((line) => String(line).trim()).length === 0;
      return value === null || value === undefined || String(value).trim() === '';
    })
    .map((field) => field.label));
}

/** A Location carries an address line or a building name, and one with neither is refused (decision 0673). */
function locationNameProblems(values: Readonly<Record<string, unknown>>): string[] {
  const lines = Array.isArray(values.addressLines) ? values.addressLines : typeof values.addressLines === 'string' ? [values.addressLines] : [];
  const addressed = lines.some((line) => String(line ?? '').trim() !== '');
  const named = typeof values.buildingName === 'string' && values.buildingName.trim() !== '';
  return addressed || named ? [] : ['Address or Building name'];
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** What the Deceased preview answers: the internal people notified, by name, and the active employments the save ends (decision 0169). */
export interface DeceasedPreviewAnswer {
  recipients: readonly { personId: string; name: string; email?: string | null }[];
  employments: number;
}

/** True where a Person save is the transition into Deceased, the one status move that confirms first (decision 0169). */
export function isDeceasedTransition(recordType: PagedRecord, before: unknown, after: unknown): boolean {
  return recordType === 'PersonRecord' && after === 'Deceased' && before !== 'Deceased';
}

/**
 * The confirm dialog's consequence: this will notify 7 people and end 2
 * employments, the names listed under it. 🔨 The blast radius is named before
 * the save, never after (decision 0169).
 */
export function deceasedConsequence(preview: DeceasedPreviewAnswer): { body: string; recordNames: string[] } {
  const n = preview.recipients.length;
  const m = preview.employments;
  const notify = n === 0 ? t('deceased.notifyNobody') : `${t('deceased.notify')} ${n} ${t(n === 1 ? 'deceased.person' : 'deceased.people')}`;
  const end = `${t('deceased.end')} ${m} ${t(m === 1 ? 'deceased.employment' : 'deceased.employments')}`;
  return { body: `${notify} ${end}.`, recordNames: preview.recipients.map((r) => r.name) };
}

/** What the Company delete preview answers (piece 12): the sites and employments the delete ends, and the open Deals it leaves. */
export interface CompanyDeletePreviewAnswer {
  displayName: string;
  sites: number;
  employments: number;
  openDeals: number;
}

/**
 * The typed-phrase dialog's subject: the Company named, the recoverable
 * delete told apart from the end-dates that are not (piece 12). 💰 The verb
 * and the counts must agree with what the cascade does: the Company goes to
 * the trash, the sites and employments end today and stay ended.
 */
export function companyDeleteSubject(preview: CompanyDeletePreviewAnswer): string {
  const count = (n: number, one: StringKey, many: StringKey) => `${n} ${t(n === 1 ? one : many)}`;
  return `${t('companyDelete.company')} “${preview.displayName}”. ${t('companyDelete.recoverable')} ${count(preview.sites, 'companyDelete.site', 'companyDelete.sites')} ${t('companyDelete.and')} ${count(preview.employments, 'companyDelete.employment', 'companyDelete.employments')} ${t('companyDelete.endToday')} ${count(preview.openDeals, 'companyDelete.openDeal', 'companyDelete.openDeals')} ${t('companyDelete.dealsStay')}`;
}

/**
 * What the Person delete preview answers (decision 7710): the employments and
 * the logins the delete ends, the open approvals and work-order assignments it
 * leaves, and the sentence that refuses it where it is refused.
 */
export interface PersonDeletePreviewAnswer {
  displayName: string;
  employments: number;
  logins: number;
  openApprovals: number;
  openAssignments: number;
  /** 🔒 Decision 9740: the Deals they own at any stage but Won or Lost, which go to the successor the window names. */
  openDeals: number;
  refusal: string | null;
}

/**
 * The typed-phrase dialog's subject for a Person (decision 7710): the Person
 * named, the recoverable delete told apart from the employments and logins
 * that end and stay ended, and what stays counted.
 */
export function personDeleteSubject(preview: PersonDeletePreviewAnswer, successorName: string | null = null): string {
  const count = (n: number, one: StringKey, many: StringKey) => `${n} ${t(n === 1 ? one : many)}`;
  const logins = preview.logins === 0 ? t('personDelete.noLogin') : count(preview.logins, 'personDelete.loginEnds', 'personDelete.loginsEnd');
  // 🔒 Decision 9740: the open Deals and who takes them are named before the typed phrase, never after.
  const deals = (preview.openDeals ?? 0) > 0 && successorName ? ` ${personDeleteDeals(preview.openDeals)} ${fill(t('personDelete.dealsGo'), { name: successorName })}` : '';
  return `${t('personDelete.person')} “${preview.displayName}”. ${t('personDelete.recoverable')} ${count(preview.employments, 'personDelete.employmentEnds', 'personDelete.employmentsEnd')} ${logins} ${count(preview.openApprovals, 'personDelete.approval', 'personDelete.approvals')} ${t('personDelete.and')} ${count(preview.openAssignments, 'personDelete.assignment', 'personDelete.assignments')} ${t('personDelete.stay')}${deals}`;
}

/** 🔒 Decision 9740: a count of open Deals in words, *1 open Deal* or *2 open Deals*. */
export function personDeleteDeals(n: number): string {
  return `${n} ${t(n === 1 ? 'personDelete.openDeal' : 'personDelete.openDeals')}`;
}
